import { useEffect, useState } from "react";
import api, { fmtINRFull, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2, Plus, Save } from "lucide-react";

export default function Thresholds() {
  const [items, setItems] = useState([]);
  const [budgetCap, setBudgetCap] = useState(0);
  const [msg, setMsg] = useState("");
  const load = async () => {
    const [t, s] = await Promise.all([api.get("/thresholds", { params: { scope: "global" } }), api.get("/settings")]);
    setItems(t.data || []);
    setBudgetCap(s.data?.budget_cap || 0);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/thresholds", { scope: "global", project_id: null, min_value: 0, max_value: 1, approver_role: "auto" });
      load();
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };
  const saveRow = async (t) => {
    try { await api.patch(`/thresholds/${t.id}`, { scope: t.scope, project_id: t.project_id, min_value: parseFloat(t.min_value), max_value: parseFloat(t.max_value), approver_role: t.approver_role }); setMsg("Saved."); load(); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (t) => { await api.delete(`/thresholds/${t.id}`); load(); };
  const saveCap = async () => {
    try { await api.patch("/settings", { budget_cap: parseFloat(budgetCap) }); setMsg("Budget cap saved."); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">Approval Thresholds</h1>
        <p className="mt-1.5 text-slate-500">Indent routing bands and the budget cap that escalates project budgets to the Chairman.</p>
      </div>
      {msg && <div data-testid="threshold-msg" className="text-sm text-slate-600">{msg}</div>}

      <Card className="p-5 border-slate-200 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold tracking-widest uppercase text-slate-500">Budget cap</div>
            <div className="text-xs text-slate-500 mt-1">Project budgets above this amount route to the Chairman for approval.</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Input data-testid="budget-cap-input" type="number" value={budgetCap} onChange={(e) => setBudgetCap(e.target.value)} className="max-w-[240px]" />
          <div className="text-sm text-slate-600">= {fmtINRFull(parseFloat(budgetCap) || 0)}</div>
          <Button data-testid="save-cap-btn" onClick={saveCap} className="rounded-full bg-[#0D3B66] hover:bg-[#114B82]"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
        </div>
      </Card>

      <Card className="p-5 border-slate-200 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold tracking-widest uppercase text-slate-500">Indent routing bands (global)</div>
            <div className="text-xs text-slate-500 mt-1">Indents are routed by value. First matching band wins.</div>
          </div>
          <Button data-testid="add-threshold-btn" onClick={add} variant="outline" className="rounded-full"><Plus className="h-4 w-4 mr-1.5" /> Add band</Button>
        </div>
        <div className="space-y-2">
          <div className="hidden md:grid md:grid-cols-12 gap-2 text-[11px] uppercase font-semibold text-slate-500 tracking-widest px-2">
            <div className="col-span-3">Min</div><div className="col-span-3">Max</div><div className="col-span-4">Approver</div><div className="col-span-2 text-right">Action</div>
          </div>
          {items.map((t, idx) => (
            <ThresholdRow key={t.id} t={t} onChange={(nt) => { const c = [...items]; c[idx] = nt; setItems(c); }} onSave={() => saveRow(items[idx])} onDelete={() => del(t)} />
          ))}
          {items.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No bands. Add one to start routing indents.</div>}
        </div>
      </Card>
    </div>
  );
}

function ThresholdRow({ t, onChange, onSave, onDelete }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded-lg p-2">
      <Input data-testid={`th-min-${t.id}`} className="col-span-3 h-9" type="number" value={t.min_value} onChange={(e) => onChange({ ...t, min_value: e.target.value })} />
      <Input data-testid={`th-max-${t.id}`} className="col-span-3 h-9" type="number" value={t.max_value} onChange={(e) => onChange({ ...t, max_value: e.target.value })} />
      <div className="col-span-4">
        <Select value={t.approver_role} onValueChange={(v) => onChange({ ...t, approver_role: v })}>
          <SelectTrigger data-testid={`th-role-${t.id}`} className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto-approve</SelectItem>
            <SelectItem value="engineering_head">Engineering Head</SelectItem>
            <SelectItem value="chairman">Chairman</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1">
        <Button data-testid={`th-save-${t.id}`} onClick={onSave} size="sm" variant="outline" className="rounded-full">Save</Button>
        <button data-testid={`th-del-${t.id}`} onClick={onDelete} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
